import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "packing-europe")!;

// Section accents pulled from the brand --gt-feature-* palette, same
// scheme as the nav guides and toll-roads guide.
const ACCENT = {
    teal: { fill: "#0f4c5c", chipBg: "rgba(15,76,92,.12)", chipText: "#0f4c5c" },
    rust: { fill: "#c1654a", chipBg: "rgba(193,101,74,.13)", chipText: "#a1462c" },
    slate: { fill: "#5a6772", chipBg: "rgba(90,103,114,.13)", chipText: "#485460" },
    green: { fill: "#5b8a72", chipBg: "rgba(91,138,114,.14)", chipText: "#3f6b53" },
    plum: { fill: "#7a4f6d", chipBg: "rgba(122,79,109,.14)", chipText: "#5f3c54" },
} as const;

type Item = { title: string; body: string };
type Section = {
    catnum: string;
    chipLabel: string;
    accent: keyof typeof ACCENT;
    heading: string;
    intro?: string;
    items: Item[];
};

const SECTIONS: Section[] = [
    {
        catnum: "01",
        chipLabel: "Get these right or it ends at the border",
        accent: "teal",
        heading: "Documents — get these right or the trip ends at the border",
        intro:
            "Post-Brexit, UK riders need more paper than they used to. Check everything two weeks before you leave, not the night before.",
        items: [
            {
                title: "Passport",
                body:
                    "Must be valid for at least 3 months after the day you plan to leave the Schengen area, and issued within the last 10 years on the date you enter. The Schengen 90-in-180 rule applies — count carefully if you've been over recently.",
            },
            {
                title: "Driving licence",
                body:
                    "UK photocard licence is accepted in all four countries. You no longer need the paper counterpart. An International Driving Permit (1968 Vienna type) is technically not required for short visits but costs £5.50 at the Post Office and removes any roadside argument.",
            },
            {
                title: "V5C log book (vehicle registration)",
                body:
                    "Carry the original, not a copy. Police can ask for it at any roadside check, particularly in France and Italy. If the bike isn't in your name, also carry a signed letter of authority from the registered keeper.",
            },
            {
                title: "Motor insurance certificate",
                body:
                    "Confirm with your insurer that European cover is included for the dates you'll be away — some UK policies default to third-party only abroad. Carry the certificate as a paper document; PDFs on a phone are not always accepted.",
            },
            {
                title: "Green Card",
                body:
                    "Strictly no longer required for UK insurers since 2021, but a few European forces still ask for it. Cheap to print, easy to carry — worth having.",
            },
            {
                title: "GHIC card",
                body:
                    "Replaces the old EHIC for state healthcare in the EU and Switzerland. It is NOT a substitute for travel insurance — get a proper policy that covers motorcycling and repatriation.",
            },
            {
                title: "Breakdown cover paperwork",
                body:
                    "AA, RAC and Green Flag all sell pan-European cover for bikes. Carry the policy number and the 24-hour callout phone number on paper — you can't always rely on signal in the Alps.",
            },
            {
                title: "Photocopies",
                body:
                    "Take a photo of every document on your phone, plus a paper copy stowed in a different pannier from the originals.",
            },
        ],
    },
    {
        catnum: "02",
        chipLabel: "Layer for the worst day",
        accent: "rust",
        heading: "Riding gear",
        intro:
            "Mountain roads + summer storms + autoroute slabs all in one week. Layer for the worst day you might hit, not the average.",
        items: [
            {
                title: "Helmet",
                body:
                    "ECE 22.06 or DOT certified. A clear visor for evening riding through tunnels and tinted (or a Pinlock with sunglasses) for daytime alpine glare. Spare visor or anti-fog insert if you're heading above 1,500m.",
            },
            {
                title: "Jacket and trousers",
                body:
                    "CE Level AA rated minimum, with armour at shoulders, elbows, hips and knees. A back protector that goes the full length of your spine. Vents you can open while riding, because by the time you stop in 35°C heat it's too late.",
            },
            {
                title: "Boots and gloves",
                body:
                    "Ankle-height boots with non-slip oil-resistant sole and a hard heel cup. Two pairs of gloves — light summer mesh and waterproof mid-weight — because you will hit rain even in July.",
            },
            {
                title: "Waterproof oversuit",
                body:
                    "Goretex jackets are not as waterproof as the brochures say after the first hour. A cheap oversuit packed in a tank bag will save the whole trip when a storm hits the Swiss passes.",
            },
            {
                title: "Layers",
                body:
                    "A thin merino base layer, a fleece mid-layer, and a windproof gilet covers everything from 5°C alpine mornings to 32°C Italian afternoons. Avoid cotton — it stays wet.",
            },
        ],
    },
    {
        catnum: "03",
        chipLabel: "Enough to get you rolling again",
        accent: "slate",
        heading: "Tools, spares, and roadside fixes",
        intro:
            "You don't need to carry a full workshop, but a couple of tubeless tyre plugs and a 12V pump have saved more European tours than any factory toolkit.",
        items: [
            {
                title: "Tubeless tyre repair kit",
                body:
                    "A 'string' or 'mushroom' plug kit and a compact 12V tyre inflator. A puncture in the Stelvio is otherwise a tow truck, a long wait, and a missed ferry.",
            },
            {
                title: "Chain lube and cleaner",
                body:
                    "200ml of wet-lube and a brush. After two days of alpine rain a dry chain will start to crunch. Re-lube every refuel stop in the wet.",
            },
            {
                title: "Spare bulbs and fuses",
                body:
                    "Headlight, indicator and brake bulbs, plus a spare main fuse. A blown headlight bulb at dusk in Italy is a €100 on-the-spot fine.",
            },
            {
                title: "Multi-tool, cable ties, duct tape",
                body:
                    "A small Leatherman, a dozen heavy-duty cable ties, and a half-roll of gaffer tape rolled around a Sharpie. 90% of roadside fixes start with these.",
            },
            {
                title: "Cable repair kit",
                body:
                    "A universal emergency kit — spare inner cable and a couple of screw-on nipples — packs down to nothing. Clutch and throttle cables almost always snap at the nipple, and when one goes it's the difference between a 20-minute lay-by fix and a recovery truck home. Get a proper replacement cable fitted at the next town; the kit's job is just to get you rolling again.",
            },
            {
                title: "Tow strap or roadside hi-viz triangle (optional)",
                body:
                    "If you're riding with others, a 3m tow strap is worth its weight. The triangle isn't required on a bike but earns you better treatment from local recovery if you get a soft-shoulder breakdown.",
            },
        ],
    },
    {
        catnum: "04",
        chipLabel: "Turns, power, staying found",
        accent: "green",
        heading: "Navigation and tech",
        items: [
            {
                title: "Phone with offline maps",
                body:
                    "Download Google Maps offline for your route + 30km either side BEFORE you leave the UK. Add Calimoto or Kurviger if you want curve-optimised routing. Both work offline once the region is downloaded.",
            },
            {
                title: "Phone mount and 12V to USB charger",
                body:
                    "A vibration-damped mount (Quad Lock with the vibration damper is the gold standard — the cheap Amazon ones will literally shake the camera sensor out of your phone). A 12V to USB charger wired to the bike, with a second port for an action camera.",
            },
            {
                title: "Sat nav (optional)",
                body:
                    "A dedicated unit is unnecessary if your phone has a sealed case, but useful if you don't want to drain the phone's battery. TomTom Rider and Garmin Zumo both still hold up.",
            },
            {
                title: "Bluetooth intercom",
                body:
                    "Cardo Packtalk or Sena 50S — universal connect makes group rides much less stressful, and a podcast on a 5-hour motorway day saves your sanity.",
            },
            {
                title: "Power bank",
                body:
                    "10,000 mAh in a waterproof pouch. Phones run hot on the bars and battery degrades quickly — a top-up at lunch is the difference between navigation and lost.",
            },
        ],
    },
    {
        catnum: "05",
        chipLabel: "Make the long days bearable",
        accent: "plum",
        heading: "Comfort — make the long days bearable",
        items: [
            {
                title: "Earplugs",
                body:
                    "The single biggest upgrade you can buy. Custom-moulded ones from a hearing centre cost ~£60 and last a decade. Disposable foam works for the first trip.",
            },
            {
                title: "Buff or neck warmer",
                body:
                    "Cuts wind around the collar at altitude, doubles as a sweatband and dust mask. Bring two.",
            },
            {
                title: "Sunscreen",
                body:
                    "Factor 50 on the back of the neck. Alpine UV at 2,000m strips a layer of skin off in a day.",
            },
            {
                title: "Cushion or gel pad (optional)",
                body: "Airhawk or similar. You don't think you need it until day four.",
            },
        ],
    },
    {
        catnum: "06",
        chipLabel: "Spend, and stay well",
        accent: "teal",
        heading: "Money, banking, medical",
        items: [
            {
                title: "Cards",
                body:
                    "A no-fee travel card (Wise, Revolut, Chase) for everyday spend. Carry a backup debit card and a low-limit credit card, stored in a separate pannier from the main wallet. Notify your bank of your travel dates if you don't.",
            },
            {
                title: "Cash",
                body:
                    "€100–200 in low denominations, plus CHF 50–100 for Switzerland. Some Swiss mountain refuges still don't take cards. Toll booths on the Italian autostrada now accept contactless on every gantry — cash isn't strictly needed there.",
            },
            {
                title: "Medications",
                body:
                    "Personal prescriptions in original packaging with a copy of the prescription. A small kit with paracetamol, ibuprofen, antihistamines, plasters, blister pads, anti-diarrhoea, and rehydration sachets covers most trip dramas. A tick remover if you're heading into rural Germany or southern France.",
            },
            {
                title: "Travel insurance",
                body:
                    "Read the small print — many policies exclude motorcycling above 125cc or as a 'hazardous activity'. Check that engine size, repatriation, and bike recovery are all included. Devitt, Bennetts and Carole Nash all sell motorcycle-specific cover.",
            },
        ],
    },
    {
        catnum: "07",
        chipLabel: "Ten minutes that save the tour",
        accent: "rust",
        heading: "The day before — final checks",
        items: [
            {
                title: "Tyres",
                body:
                    "Pressure to the loaded value (usually +2 PSI front, +4 PSI rear vs solo). Inspect for embedded stones and check tread depth on both sides — front tyres wear unevenly when carrying weight.",
            },
            {
                title: "Chain and sprockets",
                body:
                    "Tension, lube, and a quick look at the rear sprocket teeth. A chain that's marginal at home will be done by the time you get to Innsbruck.",
            },
            {
                title: "Brakes",
                body:
                    "Pads, fluid level, and brake light operation. Alpine descents will use 2–3mm of pad per day.",
            },
            {
                title: "Lights",
                body:
                    "Headlight (main and dip), indicators front and rear, brake lights both from front and rear brake. Number plate light if your bike has one — Italian police check this.",
            },
            {
                title: "Fuel and tools",
                body:
                    "Fill up before you cross the Channel; UK fuel is usually cheaper than Calais. Run through your toolkit and check the multi-tool, plug kit, and pump are all in the bike and accessible.",
            },
        ],
    },
];

const COUNTRY_KIT = [
    {
        flag: "🇫🇷",
        name: "France",
        body:
            "A high-visibility vest, reachable WITHOUT opening luggage (i.e. under the seat or in a tank bag). Reflective stickers on all four sides of the helmet — French law requires them; non-residents are also stopped occasionally. The breathalyzer requirement was dropped in 2020 — you do not need to carry one. Headlights on, day and night.",
    },
    {
        flag: "🇩🇪",
        name: "Germany",
        body:
            "A DIN 13164-compliant first aid kit is required (a small ~£15 kit; available from any German service station). A warning triangle isn't required on a motorbike but you'll often see riders carrying one. Reflective vest is recommended but not legally required for bikes — useful if you ever stop on the autobahn.",
    },
    {
        flag: "🇨🇭",
        name: "Switzerland",
        body:
            "Motorway vignette (toll sticker) for any bike on Swiss motorways. From 2024 it's also available as an e-vignette online (~CHF 40), but the physical sticker still works fine — buy it at any service station before the border, or just inside. Lights on at all times. Switzerland uses Swiss Francs, NOT euros, even though prices are sometimes quoted in both.",
    },
    {
        flag: "🇮🇹",
        name: "Italy",
        body:
            "Reflective vest MUST be worn if you stop at the roadside or on the hard shoulder. Daytime running lights are mandatory on all roads outside urban areas. Italian police actively check documents — carry the originals, not photocopies. The autostrada toll booths accept card; pick the 'carte/cards' lane to avoid the cash queue.",
    },
];

function PackingGuideEurope() {
    usePageMeta({
        title:
            "What to pack for a motorbike tour in France, Germany, Switzerland and Italy — Planamoto",
        description:
            "A rider's checklist for European motorbike touring. Documents, country-specific legal kit, gear, tools, tech, medical and the day-before checks for France, Germany, Switzerland and Italy.",
        url: "https://www.planamoto.com/guides/packing-europe",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="What to pack for a motorbike tour in France, Germany, Switzerland and Italy"
            lede="Cross-border riding in Europe is easy once you've done it — but every country has its own quirks, and the consequences of arriving without a hi-viz vest, the right documents, or the right insurance can ruin a holiday. This is the kit list we'd take on a two-week loop of the Alps and the Black Forest. Don't take everything; do check every section."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "Motorbike loaded with panniers and a tank bag ready for a tour",
            }}
            beforeSections={
                <>
                    <GuideStackLead
                        eyebrow="What I never leave without"
                        authorNote="Spencer · after a lot of tours getting this wrong"
                        heading="The stuff that ends a trip if you forget it"
                        intro="Take everything below and you'll be over-packed; skip the wrong item and you're fined at a French péage or turned back at the border. These three I check first, every single time — the full list follows."
                        columns={[
                            {
                                label: "01 · Documents",
                                title: "V5C, insurance, passport",
                                body: "Original V5C, insurance certificate on paper, passport valid 3 months past your return. Photograph everything; keep a paper copy in a different pannier.",
                            },
                            {
                                label: "02 · Legal kit",
                                title: "The per-country kit",
                                body: "Hi-viz reachable without opening luggage, helmet reflectives for France, a DIN first-aid kit for Germany, a Swiss vignette. Buy once, leave it in the bike.",
                            },
                            {
                                label: "03 · The oversuit",
                                title: "A cheap waterproof",
                                body: "One in the tank bag. Goretex stops being waterproof after an hour; a storm on the Swiss passes is a certainty, not a risk.",
                            },
                        ]}
                        backstop="run Planamoto's printable packing checklist the night before and tick it off as it goes in the panniers. The list you don't write is the item you forget."
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The full kit list
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            That's the top three. Here's everything, section by section.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            Everything worth carrying, grouped the way you'd pack it —
                            with the legal must-haves flagged, not buried. Don't take all
                            of it; do check every section.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <GuideSection
                index={1}
                catnum={SECTIONS[0].catnum}
                heading={SECTIONS[0].heading}
                intro={SECTIONS[0].intro}
                chipLabel={SECTIONS[0].chipLabel}
                accent={ACCENT[SECTIONS[0].accent]}
                items={SECTIONS[0].items.map((it) => ({ ...it, marker: "✓" }))}
            />
            <GuideSection
                index={2}
                heading="Country-specific kit you must carry by law"
                intro="Each country has its own rules. Buying once and keeping the kit in the bike for every trip is easier than checking before each crossing."
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                items={COUNTRY_KIT.map((c) => ({
                    title: c.name,
                    body: c.body,
                    marker: c.flag,
                }))}
            />
            {SECTIONS.slice(1).map((s, i) => (
                <GuideSection
                    key={s.heading}
                    index={i + 3}
                    catnum={s.catnum}
                    heading={s.heading}
                    intro={s.intro}
                    chipLabel={s.chipLabel}
                    accent={ACCENT[s.accent]}
                    items={s.items.map((it) => ({ ...it, marker: "✓" }))}
                />
            ))}

            <GuideCtaBand
                heading="We handle the plan and the checklist. You handle the panniers."
                body="Build the route, split the expenses, and run a shared packing checklist the whole group ticks off before you leave — nobody arrives at Folkestone without their V5C. Clone a hand-curated Alps tour as your starting point."
            />

            <RelatedGuides
                slugs={[
                    "cycle-touring-packing",
                    "french-toll-roads",
                    "bike-security",
                ]}
            />
            <CtaCard
                title="Plan the route too"
                body="Once the kit is sorted, the route is the fun part. Browse our hand-curated multi-day tours and clone one as your starting point."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default PackingGuideEurope;
